import React, { useEffect, useRef, useState } from 'react'
import videojs from 'video.js'
import 'video.js/dist/video-js.css'
import 'videojs-contrib-quality-levels'

// --- Custom Resolution Selector Component ---
const MenuButton = videojs.getComponent('MenuButton');
const MenuItem = videojs.getComponent('MenuItem');

class ResolutionMenuItem extends MenuItem {
  constructor(player, options) {
    // Label is what shows in the menu
    options.label = options.label || 'Unknown';
    options.selectable = true;
    super(player, options);
    this.plugin = options.plugin;
    this.level = options.level; // The specific quality level (or null for auto)
  }

  handleClick(event) {
    super.handleClick(event);

    // Update Quality Level
    const levels = this.player_.qualityLevels();

    if (this.options_.value === 'auto') {
      // Enable all for auto
      for (let i = 0; i < levels.length; i++) {
        levels[i].enabled = true;
      }
      console.log('Set quality to AUTO');
    } else {
      // Enable only the selected one
      for (let i = 0; i < levels.length; i++) {
        levels[i].enabled = (i === this.options_.levelIndex);
      }
      // console.log('Set quality to:', this.options_.label);
    }

    // We rely on smooth quality change, so no forced seek is needed here.
    // The player will switch to the new quality after the current buffer is consumed.
  }
}

class ResolutionMenuButton extends MenuButton {
  constructor(player, options) {
    super(player, options);
    this.addClass('vjs-resolution-button');
    this.controlText('Quality');

    // Re-build menu when levels change
    player.qualityLevels().on(['change', 'addqualitylevel', 'removequalitylevel'], () => {
      this.update();
    });
  }

  buildCSSClass() {
    return `vjs-resolution-button ${super.buildCSSClass()}`;
  }

  createItems() {
    const player = this.player_;
    const levels = player.qualityLevels();
    const items = [];

    if (!levels || levels.length === 0) return items;

    // Add 'Auto' option
    items.push(new ResolutionMenuItem(player, {
      label: 'Auto',
      value: 'auto',
      selected: true, // Default to true initially, logic to update selection is complex but Auto is safe default
      plugin: this
    }));

    // Add specific levels (High to Low)
    for (let i = levels.length - 1; i >= 0; i--) {
      const level = levels[i];
      let label = '';

      if (level.height) label = `${level.height}p`;
      else if (level.bitrate) label = `${Math.floor(level.bitrate / 1000)}kbps`;
      else label = `Level ${i}`;

      items.push(new ResolutionMenuItem(player, {
        label: label,
        value: 'level',
        levelIndex: i,
        selected: false,
        plugin: this
      }));
    }

    return items;
  }
}

// Register the component
videojs.registerComponent('ResolutionMenuButton', ResolutionMenuButton);


const VideoPlayer = ({ src, thumbnail, autoPlay = false }) => {
  const videoNode = useRef(null)
  const playerRef = useRef(null)
  const [isFallback, setIsFallback] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [isPlayerActive, setIsPlayerActive] = useState(autoPlay)

  // Create fallback URL from src if possible
  const fallbackSrc = src ? src.replace(/\/hls\/master\.m3u8$/, '/fallback.mp4') : ''

  // Initialize Player
  useEffect(() => {
    // Wait for user interaction or autoPlay
    if (!isPlayerActive) return
    if (!videoNode.current) return

    // Video.js options
    const videoJsOptions = {
      autoplay: autoPlay, // Use the prop value
      muted: autoPlay, // Many browsers require muted for autoplay
      controls: true,
      responsive: true,
      fill: true,
      preload: autoPlay ? 'auto' : 'none', // Preload if autoplaying
      poster: thumbnail || "/icons/No-postimage-icon.jpeg",
      sources: [{
        src: isFallback ? fallbackSrc : src,
        type: (src && (src.includes('.m3u8') || src.includes('master.m3u8'))) && !isFallback ? 'application/x-mpegURL' : 'video/mp4'
      }],
      // YouTube-like Control Bar Layout (matching YouTube exactly)
      controlBar: {
        children: [
          'playToggle',
          'currentTimeDisplay',
          'timeDivider',
          'durationDisplay',
          'progressControl',
          // Everything after this spacer goes to the right
          'volumePanel',
          'ResolutionMenuButton',
          'fullscreenToggle',
        ],
        volumePanel: {
          inline: true
        }
      },
      html5: {
        hls: {
          enableLowInitialPlaylist: true,
          smoothQualityChange: true,
          overrideNative: true,
        },
      },
      userActions: {
        hotkeys: true
      },
    }

    // Initialize or Update Player
    if (!playerRef.current) {
      const player = playerRef.current = videojs(videoNode.current, videoJsOptions, () => {
        console.log('Video.js player is ready')
      })

      // Error Handling & Fallback Logic
      player.on('loadedmetadata', () => {
        const levels = player.qualityLevels();
        console.log('[DEBUG] Metadata Loaded. Quality Levels:', levels.length, levels);

        // Check if our custom button is there
        setTimeout(() => {
          const btn = player.controlBar.getChild('ResolutionMenuButton');
          if (btn) {
            console.log('[DEBUG] ResolutionMenuButton active.');
            if (levels.length <= 1) {
              btn.hide();
              // When hidden, ensure it doesn't leave a gap or weird state
              btn.addClass('vjs-hidden');
            } else {
              btn.show();
              btn.removeClass('vjs-hidden');
            }
          }
        }, 500);
      });

      player.on('error', () => {
        const error = player.error()
        console.error('Video.js Error:', error)

        if (!isFallback && src.includes('.m3u8')) {
          console.log('Switching to fallback MP4...')
          setIsFallback(true)
        } else {
          setHasError(true)
        }
      })

      // Styling fixes
      player.addClass('vjs-custom-theme')
      player.addClass('vjs-big-play-centered') // Native VJS class for centering

    } else {
      // Update existing player
      const player = playerRef.current

      // Load new source
      player.src({
        src: isFallback ? fallbackSrc : src,
        type: (src && (src.includes('.m3u8') || src.includes('master.m3u8'))) && !isFallback ? 'application/x-mpegURL' : 'video/mp4'
      })

      if (autoPlay || isFallback) {
        player.play().catch(e => console.log('Autoplay prevented', e))
      }
    }

  }, [src, isFallback, autoPlay, thumbnail, fallbackSrc, isPlayerActive])

  // Cleanup
  useEffect(() => {
    return () => {
      if (playerRef.current) {
        playerRef.current.dispose()
        playerRef.current = null
      }
    }
  }, [])

  return (
    <div className="video-player-wrapper" style={{
      width: '100%',
      height: '100px',
      minHeight: '400px',
      position: 'relative',
      borderRadius: '12px',
      overflow: 'hidden',
      backgroundColor: '#000',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }}>
      {/* Facade: Show only image + play button until clicked */}
      {!isPlayerActive ? (
        <div
          onClick={() => setIsPlayerActive(true)}
          style={{
            width: '100%',
            height: '100%',
            backgroundImage: `url(${thumbnail || "/icons/No-postimage-icon.jpeg"})`,
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundColor: '#000',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative'
          }}
        >
          {/* Fake Big Play Button matching Custom Theme */}
          <div style={{
            backgroundColor: 'rgba(33, 33, 33, 0.8)',
            border: 'none',
            borderRadius: '12px',
            width: '68px',
            height: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s',
            color: 'white'
          }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#ff0000'; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(33, 33, 33, 0.8)'; }}
          >
            <svg viewBox="0 0 24 24" fill="white" width="32" height="32">
              <path d="M8 5v14l11-7z" />
            </svg>
          </div>
        </div>
      ) : (
        <div data-vjs-player style={{ width: '100%', height: '100%' }}>
          <video
            ref={videoNode}
            className="video-js vjs-fill"
            playsInline
            preload="none"
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        </div>
      )}

      {hasError && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          color: 'white',
          textAlign: 'center',
          pointerEvents: 'none'
        }}>
          <p>Video failed to load.</p>
        </div>
      )}

      {/* Custom Styles for YouTube look */}
      <style>{`
        /* Font */
        .video-player-wrapper .video-js {
            font-family: 'Roboto', 'Arial', sans-serif;
        }

        /* YouTube Layout: Push volume to the right */
        .vjs-custom-theme .vjs-volume-panel {
            margin-left: auto !important;
            margin-right: 8px;
        }

        /* --- Resolution Button Custom Icon (HD Cog) --- */
        .vjs-resolution-button .vjs-icon-placeholder:before {
             content: "\\f114"; /* Cog Icon */
             font-family: VideoJS;
             font-size: 1.8em;
        }
        
        .vjs-resolution-button .vjs-menu-item {
            text-transform: capitalize;
        }

        /* --- Big Play Button (YouTube Style) --- */
        .vjs-custom-theme .vjs-big-play-button {
            border: none;
            background-color: rgba(33, 33, 33, 0.8) !important; /* Dark background */
            border-radius: 12px;
            width: 68px;
            height: 48px;
            line-height: 48px;
            font-size: 3em; 
            transition: all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1);
            /* Center it handled by vjs-big-play-centered but we enforce */
            margin-left: -34px;
            margin-top: -24px;
        }

        .vjs-custom-theme:hover .vjs-big-play-button {
            background-color: #ff0000 !important; /* YouTube Red */
            opacity: 1;
            transform: scale(1.1);
        }
        
        /* Remove the weird circle border if present in theme */
        .video-js .vjs-big-play-button .vjs-icon-placeholder:before {
            content: ""; /* We can replace icon or let it be */
        }
        /* Custom Play Icon (Triangle) */
        .vjs-custom-theme .vjs-big-play-button:before {
            content: "▶"; /* Simple unicode or keep default icon */
            font-family: VideoJS;
            content: "\\f101"; /* VideoJS Play Icon */
            font-size: 0.6em; /* Adjust size inside box */
            line-height: 48px;
        }

        /* --- Control Bar --- */
        .vjs-custom-theme .vjs-control-bar {
            background: linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0.5) 40%, rgba(0,0,0,0) 100%);
            height: 48px;
            display: flex;
            align-items: center;
            opacity: 0;
            transition: opacity 0.2s;
            z-index: 10 !important;
        }
        .vjs-custom-theme.vjs-has-started.vjs-user-active .vjs-control-bar,
        .vjs-custom-theme.vjs-paused .vjs-control-bar {
            opacity: 1;
        }

        /* --- Progress Bar (Scrubber) --- */
        .vjs-custom-theme .vjs-progress-control {
            position: absolute;
            top: -10px;
            left: 10px;
            right: 10px;
            width: auto;
            height: 5px;
        }
        .vjs-custom-theme .vjs-progress-control:hover {
            height: 8px; /* Expand on hover */
            top: -13px;
        }
        
        /* Background */
        .vjs-custom-theme .vjs-slider {
            background-color: rgba(255,255,255,0.3);
        }
        
        /* Load Progress */
        .vjs-custom-theme .vjs-load-progress {
            background-color: rgba(255,255,255,0.6);
        }
        
        /* Play Progress (Red) */
        .vjs-custom-theme .vjs-play-progress {
            background-color: #ff0000;
            transition: width 0.1s linear;
        }
        .vjs-custom-theme .vjs-play-progress:before {
            display: none; /* Hide default circle */
        }
        
        /* Scrubber handle on hover */
        .vjs-custom-theme .vjs-progress-control:hover .vjs-play-progress:before {
            display: block;
            content: "";
            background-color: #ff0000;
            width: 14px;
            height: 14px;
            border-radius: 50%;
            top: -3px;
            right: -7px;
            box-shadow: 0 0 2px rgba(0,0,0,0.3);
        }

        /* --- Buttons & Icons --- */
        .vjs-custom-theme .vjs-button {
            color: #fff;
            width: 40px;
        }
        .vjs-custom-theme .vjs-button:hover {
            color: #fff;
            text-shadow: 0 0 5px rgba(255,255,255,0.5);
        }

        /* --- Force Time Display Visibility --- */
        .vjs-custom-theme .vjs-current-time,
        .vjs-custom-theme .vjs-time-divider,
        .vjs-custom-theme .vjs-duration {
            display: block !important;
            padding-left: 5px;
            padding-right: 5px;
        }
        
        .vjs-custom-theme .vjs-current-time-display,
        .vjs-custom-theme .vjs-duration-display {
             font-size: 14px;
             line-height: 48px;
        }

      `}</style>
    </div>
  )
}

export default VideoPlayer
